import {
  isPrintableCharacter,
  setSubmenuFocusToLastMenuItem,
  setSubmenuFocusToFirstMenuItem,
  setSubmenuFocusToNextMenuItem,
  setSubmenuFocusToPreviousMenuItem,
  setSubmenuFocusByMenuItemFirstCharacter,
} from "../helpers"

import debounce from "lodash/debounce"

const keyCodes = {
  tab: 9,
  enter: 13,
  space: 32,
  pageup: 33,
  pagedown: 34,
  end: 35,
  home: 36,
  left: 37,
  up: 38,
  right: 39,
  down: 40,
  escape: 27,
}

/** Submenu methods */
const openSubmenu = (button, submenu) => {
  button.setAttribute("aria-expanded", "true")
  button.setAttribute("aria-label", button.getAttribute("aria-label").replace(/^Open/, "Close"))
  button.parentNode.classList.add("active")

  const icon = button.querySelector("i")
  icon.textContent = "expand_less"

  submenu.classList.add("sub-nav--open")
  submenu.setAttribute("aria-hidden", "false")

  if (window.matchMedia("(min-width: 992px)").matches) {
    const body = document.querySelector("body")
    const header = document.querySelector(".main-header")
    const overlay = document.querySelector(".main-nav__overlay")

    overlay.classList.add("main-nav__overlay--visible")
    overlay.style.height = `${body.clientHeight - header.clientHeight}px`
  }
}

const closeSubmenu = (button, submenu) => {
  button.setAttribute("aria-expanded", "false")
  button.setAttribute("aria-label", button.getAttribute("aria-label").replace(/^Close/, "Open"))
  button.parentNode.classList.remove("active")

  const icon = button.querySelector("i")
  icon.textContent = "expand_more"

  submenu.classList.remove("sub-nav--open")
  submenu.setAttribute("aria-hidden", "true")

  const overlay = document.querySelector(".main-nav__overlay")
  overlay.classList.remove("main-nav__overlay--visible")
}

const closeOpenSubmenus = submenu => {
  const openSubmenus = document.querySelectorAll(".sub-nav--open")

  openSubmenus.forEach(o => {
    if (o !== submenu) {
      const btn = o.parentNode.querySelector("button")

      btn.setAttribute("aria-expanded", "false")
      btn.setAttribute("aria-label", btn.getAttribute("aria-label").replace(/^Close/, "Open"))
      btn.parentNode.classList.remove("active")

      const icon = btn.querySelector("i")
      icon.textContent = "expand_more"

      o.classList.remove("sub-nav--open")
      o.setAttribute("aria-hidden", "true")
    }
  })
}

/** Event handlers */
const handleSubmenuToggle = ev => {
  const button = ev.target
  const submenu = document.getElementById(button.getAttribute("aria-controls"))

  closeOpenSubmenus(submenu)

  if (submenu) {
    if (submenu.classList.contains("sub-nav--open")) {
      // Close submenu
      closeSubmenu(button, submenu)
    } else {
      // Open submenu
      openSubmenu(button, submenu)
    }
  }
}

const handleMenuButtonKeydown = ev => {
  let stopPropagation = false

  const button = ev.target
  const submenu = document.getElementById(button.getAttribute("aria-controls"))

  switch (ev.keyCode) {
    case keyCodes.space:
    case keyCodes.enter:
    case keyCodes.down:
      if (submenu) {
        closeOpenSubmenus(submenu)
        openSubmenu(button, submenu)
        setSubmenuFocusToFirstMenuItem(submenu)
      }

      stopPropagation = true
      break

    case keyCodes.up:
      if (submenu) {
        closeOpenSubmenus(submenu)
        openSubmenu(button, submenu)
        setSubmenuFocusToLastMenuItem(submenu)
      }

      stopPropagation = true
      break

    case keyCodes.escape:
      if (submenu) {
        closeOpenSubmenus(null)
      }

      stopPropagation = true
      break
  }

  if (stopPropagation) {
    ev.stopPropagation()
    ev.preventDefault()
  }
}

const handleMenuItemKeydown = ev => {
  let stopPropagation = false

  if (ev.ctrlKey || ev.altKey || ev.metaKey || ev.keyCode === keyCodes.space || ev.keyCode === keyCodes.enter) {
    return
  }

  const char = ev.key
  const submenu = document.querySelector(".sub-nav--open")
  const button = document.querySelector(`[aria-controls="${submenu.getAttribute("id")}"]`)

  switch (ev.keyCode) {
    case keyCodes.escape:
      if (button && submenu) {
        closeSubmenu(button, submenu)
        button.focus()
      }

      stopPropagation = true
      break

    case keyCodes.home:
    case keyCodes.pageup:
      if (submenu) {
        setSubmenuFocusToFirstMenuItem(submenu)
      }

      stopPropagation = true
      break

    case keyCodes.end:
    case keyCodes.pagedown:
      if (submenu) {
        setSubmenuFocusToLastMenuItem(submenu)
      }

      stopPropagation = true
      break

    case keyCodes.tab:
      if (button && submenu) {
        closeSubmenu(button, submenu)
        button.focus()
      }
      break

    case keyCodes.up:
      if (submenu) {
        setSubmenuFocusToPreviousMenuItem(submenu)
        stopPropagation = true
      }
      break

    case keyCodes.down:
      if (submenu) {
        setSubmenuFocusToNextMenuItem(submenu)
        stopPropagation = true
      }
      break

    default:
      if (submenu && isPrintableCharacter(char)) {
        setSubmenuFocusByMenuItemFirstCharacter(submenu, char)
      }
      break
  }

  if (stopPropagation) {
    ev.stopPropagation()
    ev.preventDefault()
  }
}

export default function init() {
  const mainNavItems = document.querySelectorAll(".main-nav__item button")
  const subNavItems = document.querySelectorAll(".sub-nav__item a")
  const subNavIntroItems = document.querySelectorAll(".sub-nav__intro a")
  const menuToggler = document.querySelector(".main-header__menu-toggler")

  const nav = document.querySelector(".main-nav")
  const footer = document.querySelector(".footer")
  const main = document.querySelector("main")
  const newsletter = document.querySelector(".newsletter-signup")
  const overlay = document.querySelector(".main-nav__overlay")

  const searchButton = document.querySelectorAll("#SearchForm_SearchForm_action_results")

  searchButton.forEach(btn => {
    btn.querySelector("span").classList.add("sr-only")
  })

  // Bind events
  mainNavItems.forEach(item => {
    item.addEventListener(
      "click",
      debounce(ev => {
        handleSubmenuToggle(ev)
      }, 150)
    )

    item.addEventListener("keydown", handleMenuButtonKeydown)
  })

  subNavItems.forEach(item => {
    item.addEventListener("keydown", handleMenuItemKeydown)
  })

  subNavIntroItems.forEach(item => {
    item.addEventListener("keydown", handleMenuItemKeydown)
  })

  menuToggler.addEventListener("click", ev => {
    const nav = document.querySelector(".main-nav")
    const icon = ev.target.querySelector("i")

    if (nav.getAttribute("aria-hidden") === "false") {
      nav.setAttribute("aria-hidden", "true")
      main.setAttribute("aria-hidden", "false")
      footer.setAttribute("aria-hidden", "false")
      newsletter.setAttribute("aria-hidden", "false")
      icon.textContent = "menu"
    } else {
      nav.setAttribute("aria-hidden", "false")
      main.setAttribute("aria-hidden", "true")
      footer.setAttribute("aria-hidden", "true")
      newsletter.setAttribute("aria-hidden", "true")
      icon.textContent = "close"
    }
  })

  overlay.addEventListener("click", ev => {
    closeOpenSubmenus(null)

    if (window.matchMedia("(min-width: 992px)").matches) {
      ev.target.classList.remove("main-nav__overlay--visible")
    }
  })

  window.addEventListener("resize", ev => {
    const icon = menuToggler.querySelector("i")

    if (
      window.matchMedia("(max-width: 991px").matches &&
      ev.target.getAttribute("id") === "SearchForm_SearchForm_Search"
    ) {
      return false
    }

    if (window.matchMedia("(max-width: 991px)").matches) {
      nav.setAttribute("aria-hidden", "true")
      icon.textContent = "menu"
      closeOpenSubmenus(null)

      if (main.getAttribute("aria-hidden") === "true") {
        main.setAttribute("aria-hidden", "false")
      }

      if (footer.getAttribute("aria-hidden") === "true") {
        footer.setAttribute("aria-hidden", "false")
      }

      if (newsletter.getAttribute("aria-hidden") === "true") {
        newsletter.setAttribute("aria-hidden", "false")
      }
    } else {
      nav.setAttribute("aria-hidden", "false")
      icon.textContent = "menu"
    }
  })

  if (window.matchMedia("(max-width: 991px)").matches) {
    nav.setAttribute("aria-hidden", "true")
  } else {
    nav.setAttribute("aria-hidden", "false")

    if (main.getAttribute("aria-hidden") === "true") {
      main.setAttribute("aria-hidden", "false")
    }

    if (footer.getAttribute("aria-hidden") === "true") {
      footer.setAttribute("aria-hidden", "false")
    }

    if (newsletter.getAttribute("aria-hidden") === "true") {
      newsletter.setAttribute("aria-hidden", "false")
    }
  }
}
